/**
 * The published year as an iCalendar file, for a phone or desktop calendar: every feast and every
 * weekly Sabbath as an all-day event, from the same published days the Calendar page draws.
 *
 * All-day rather than timed, on purpose. The biblical day begins at the previous sunset
 * (Genesis 1:5, Leviticus 23:32) and sunset depends on where the subscriber is, which a file
 * cannot know; so each event sits on the Gregorian date the published day carries and its
 * description says the day began at sunset the evening before. Timed events would be wrong for
 * everyone who is not at the location the file was made for, and wrong in a way that looks exact.
 *
 * Sabbaths are written out one by one rather than as a weekly rule, because the list of days is
 * what the app published and a rule would be the site working the calendar out for itself,
 * which the calendar module deliberately does not do.
 */
import type { PublishedDay, Feast, YearStartOption } from './calendar'
import { YEAR_START, feastsOn, ordinal } from './calendar'

const stamp = (d: string) => d.replace(/-/g, '')
const nextDay = (d: string) => { const t = new Date(`${d}T00:00:00Z`); t.setUTCDate(t.getUTCDate() + 1); return t.toISOString().slice(0, 10) }
const escape = (s: string) => s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n')
/** RFC 5545 folds lines at 75 octets; a byte-level fold keeps multi-byte characters whole. */
function fold(line: string): string {
  const bytes = new TextEncoder().encode(line)
  if (bytes.length <= 75) return line
  const out: string[] = []
  let start = 0
  while (start < bytes.length) {
    let end = Math.min(start + (start === 0 ? 75 : 74), bytes.length)
    while (end < bytes.length && (bytes[end]! & 0xc0) === 0x80) end--
    out.push((start === 0 ? '' : ' ') + new TextDecoder().decode(bytes.slice(start, end)))
    start = end
  }
  return out.join('\r\n')
}

export function buildIcs(days: PublishedDay[], feasts: Feast[], option: YearStartOption): string {
  const lines: string[] = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Covellah//Biblical Calendar//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH',
    `X-WR-CALNAME:Covellah Feasts and Sabbaths (${YEAR_START[option].label})`,
    'X-WR-CALDESC:The 364-day year of 1 Enoch 72 and Jubilees 6. Each day begins at sunset the evening before.',
  ]
  const now = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
  const event = (uid: string, start: string, endExclusive: string, summary: string, description: string) => {
    lines.push('BEGIN:VEVENT', `UID:${uid}@covellah.com`, `DTSTAMP:${now}`, `DTSTART;VALUE=DATE:${stamp(start)}`, `DTEND;VALUE=DATE:${stamp(endExclusive)}`, `SUMMARY:${escape(summary)}`, `DESCRIPTION:${escape(description)}`, 'TRANSP:TRANSPARENT', 'END:VEVENT')
  }
  // A feast runs from its start day to its end day; find the published date of each.
  const seen = new Set<string>()
  for (const d of days) {
    for (const f of feastsOn(feasts, d)) {
      if (d.dy !== f.startDay) continue
      const yearKey = `${f.id}-${d.d}`
      if (seen.has(yearKey)) continue
      seen.add(yearKey)
      const endDay = days.find((x) => x.d >= d.d && x.m === f.month && x.dy === f.endDay) ?? d
      const holy = f.sabbathDays.length ? ` Days of rest: ${f.sabbathDays.map((n) => ordinal(n)).join(', ')} of the month.` : ''
      event(`feast-${f.id}-${d.d}`, d.d, nextDay(endDay.d), f.name, `${f.description} ${f.verses.join('; ')}.${holy} ${ordinal(f.month)} month, day ${f.startDay}${f.endDay !== f.startDay ? ` to ${f.endDay}` : ''}. Begins at sunset the evening before.`)
    }
    if (d.w === 7) event(`sabbath-${d.d}`, d.d, nextDay(d.d), 'Sabbath', `The seventh day. ${ordinal(d.m)} month, day ${d.dy}. Begins at sunset the evening before.`)
    if (d.doy === 1) event(`newyear-${d.d}`, d.d, nextDay(d.d), 'Day 1 of the year', `The first day of the 364-day year (${YEAR_START[option].label}). Begins at sunset the evening before.`)
  }
  lines.push('END:VCALENDAR')
  return lines.map(fold).join('\r\n') + '\r\n'
}

export function downloadIcs(days: PublishedDay[], feasts: Feast[], option: YearStartOption): void {
  const blob = new Blob([buildIcs(days, feasts, option)], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = `covellah-calendar-${option === 'WEDNESDAY' ? 'on-after-equinox' : 'nearest-equinox'}.ics`
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
